/**
 * GroupsStep — dedicated wizard step for notification groups.
 *
 * One GroupsEditor per selected application (same binding the per-row
 * "Manage groups" dialog uses: `applicationGroups[applicationId].groups`).
 * Applications without an id yet are skipped — the step asks to pick
 * applications first when none are selected.
 */

import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Alert, Box, Typography, useTheme } from '@mui/material';
import { useTranslate } from 'react-admin';
import type { AccountFormValues, Group } from '../../types/account';
import { useAccountLookup } from '../../hooks/useAccountLookup';
import GroupsEditor from '../sections/GroupsEditor';

export const GroupsStep: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const rg = theme.custom.rg;
  const { control, setValue } = useFormContext<AccountFormValues>();
  const applications = useWatch({ control, name: 'applications' }) ?? [];
  const templatifyExtraApplicationIds =
    useWatch({ control, name: 'templatifyExtraApplicationIds' }) ?? [];
  const useTemplatify = useWatch({ control, name: 'useTemplatify' });
  const applicationGroups =
    useWatch({ control, name: 'applicationGroups' }) ?? [];
  const { data: templatifyApps = [] } = useAccountLookup('templatifyApps');

  // Applications linked either way: Account Information (notify side) or
  // the Templatify Integration multi-select. The SAME app carries a
  // different id in each system, so dedup goes by NAME (case-insensitive)
  // — the notify-side entry wins, since groups are a notify concept.
  const selectedApps: Array<{ applicationId: string; name: string }> = [];
  const seenNames = new Set<string>();
  const pushApp = (applicationId: string, name: string): void => {
    const key = name.trim().toLowerCase();
    if (seenNames.has(key)) return;
    seenNames.add(key);
    selectedApps.push({ applicationId, name });
  };
  applications
    .filter((a) => Boolean(a.applicationId))
    .forEach((a) => pushApp(a.applicationId, a.name || a.applicationId));
  if (useTemplatify) {
    templatifyExtraApplicationIds.forEach((id) => {
      const opt = templatifyApps.find((o) => o.id === id);
      pushApp(id, opt?.name ?? id);
    });
  }

  const groupsFor = (applicationId: string): Group[] =>
    applicationGroups.find((g) => g.applicationId === applicationId)?.groups ??
    [];

  const setGroupsFor = (applicationId: string, next: Group[]): void => {
    const idx = applicationGroups.findIndex(
      (g) => g.applicationId === applicationId,
    );
    const updated =
      idx >= 0
        ? applicationGroups.map((g, i) =>
            i === idx ? { ...g, groups: next } : g,
          )
        : [...applicationGroups, { applicationId, groups: next }];
    setValue('applicationGroups', updated, { shouldDirty: true });
  };

  if (selectedApps.length === 0) {
    return (
      <Alert severity="info">
        {translate('resources.accounts.groups.pickApplicationsFirst', {
          _: 'Link at least one application (Account Information or Templatify Integration) to manage its groups.',
        })}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {selectedApps.map((app) => (
        <Box key={app.applicationId}>
          <Typography
            sx={{
              fontSize: '10.5px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontWeight: 600,
              color: rg.text3,
              mb: '10px',
            }}
          >
            {app.name || app.applicationId}
          </Typography>
          <GroupsEditor
            groups={groupsFor(app.applicationId)}
            onChange={(next) => setGroupsFor(app.applicationId, next)}
            showNotice={false}
          />
        </Box>
      ))}
    </Box>
  );
};

export default GroupsStep;
